import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import {
  Activity,
  CheckSquare,
  CreditCard,
  FileSearch,
  Home,
  LineChart,
  Menu,
  Users,
  X,
} from "lucide-react";

import { Container } from "@/platform/layout/container";
import { cn } from "@/platform/lib/cn";

/**
 * Layout compartilhado para todas as rotas /admin/*. Sidebar persistente em
 * desktop + drawer mobile com hamburger. Mantém o usuário orientado mesmo
 * navegando entre seções (antes cada rota era ilha).
 */

interface AdminNavItem {
  to: string;
  label: string;
  icon: typeof Home;
  end?: boolean;
}

const NAV: AdminNavItem[] = [
  { to: "/admin", label: "Visão geral", icon: Home, end: true },
  { to: "/admin/usuarios", label: "Usuárias", icon: Users },
  { to: "/admin/assinaturas", label: "Assinaturas", icon: CreditCard },
  { to: "/admin/metricas", label: "Métricas", icon: LineChart },
  { to: "/admin/auditoria", label: "Auditoria", icon: FileSearch },
  { to: "/admin/llm-calls", label: "Telemetria LLM", icon: Activity },
  { to: "/admin/qa", label: "QA Einstein", icon: CheckSquare },
  { to: "/admin/usp/qa", label: "QA FuvestPro USP", icon: CheckSquare },
];

export function AdminShell({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="relative min-h-[calc(100vh-56px)] bg-mesh">
      <div className="flex">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-64 shrink-0 border-r border-[--color-border] bg-[color:var(--color-background)]/95 backdrop-blur sticky top-14 self-start h-[calc(100vh-56px)] overflow-y-auto">
          <SidebarNav onNavigate={() => {}} />
        </aside>

        {/* Drawer mobile */}
        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
            <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[color:var(--color-background)] shadow-2xl flex flex-col">
              <div className="flex h-14 items-center justify-between border-b border-[--color-border] px-4">
                <span className="font-display text-[length:var(--text-body-sm)] font-semibold">
                  Admin
                </span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Fechar menu"
                  className="grid h-11 w-11 place-items-center rounded-xs hover:bg-[color:var(--color-muted)]"
                >
                  <X className="size-5" aria-hidden />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <SidebarNav onNavigate={() => setMobileOpen(false)} />
              </div>
            </div>
          </div>
        )}

        {/* Conteúdo */}
        <main className="flex-1 min-w-0">
          <Container size="wide" className="py-8 md:py-12">
            <header className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setMobileOpen(true)}
                    aria-label="Abrir menu admin"
                    className="lg:hidden grid h-11 w-11 place-items-center rounded-xs hover:bg-[color:var(--color-muted)]"
                  >
                    <Menu className="size-5" aria-hidden />
                  </button>
                  <h1 className="font-display text-[length:var(--text-display-sm)] sm:text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)]">
                    {title}
                  </h1>
                </div>
                {description ? (
                  <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] max-w-2xl">
                    {description}
                  </p>
                ) : null}
              </div>
              {actions ? <div className="shrink-0">{actions}</div> : null}
            </header>

            <div className="mt-8">{children}</div>
          </Container>
        </main>
      </div>
    </div>
  );
}

function SidebarNav({ onNavigate }: { onNavigate: () => void }) {
  return (
    <nav aria-label="Áreas administrativas" className="p-4">
      <p className="px-3 py-2 text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        Admin
      </p>
      <ul className="space-y-0.5">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-[length:var(--text-body-sm)] font-medium min-h-11",
                  isActive
                    ? "bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent-soft-foreground)]"
                    : "text-[color:var(--color-muted-foreground)] hover:bg-[color:var(--color-muted)] hover:text-[color:var(--color-foreground)]",
                )
              }
            >
              <Icon className="size-4" aria-hidden />
              {label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
